import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CallSessionView } from '@/hooks/useCallSessionView';
import { formatClock } from './time';

interface Props {
  view: CallSessionView;
  onQualifyNow(call: { id: string; name: string }): void;
  /** DES-17: « Corriger » writes revision n+1 (24 h after the first commit). */
  onCorrect(call: { id: string; name: string }): void;
  onRestart(): void;
  onBackToList(): void;
}

/** Spec §4.8 (planche 4): what the session produced, and what is left to do. */
export function SessionSummary({ view, onQualifyNow, onCorrect, onRestart, onBackToList }: Props) {
  const { t } = useTranslation(['dialer']);
  const s = view.summary!;
  const duration = s.startedAt ? formatClock(s.endedAt - s.startedAt) : '—';
  const meetings = view.saved.filter((x) => x.outcome === 'meeting_booked');
  const callbacks = view.saved.filter((x) => x.outcome === 'callback_requested');
  const tiles: Array<[string, string | number]> = [
    [t('dialer:callPage.summary.calls'), view.stats.calls],
    [t('dialer:callPage.summary.connected'), view.stats.connected],
    [t('dialer:callPage.summary.meetings'), Math.max(view.stats.meetings, meetings.length)],
    [t('dialer:callPage.summary.callbacks'), Math.max(view.stats.callbacks, callbacks.length)],
    [t('dialer:callPage.summary.duration'), duration],
  ];

  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6" aria-labelledby="call-summary-title">
      <h1 id="call-summary-title" className="text-xl font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:callPage.summary.title')}</h1>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {tiles.map(([k, v]) => (
          <div key={k} className="rounded-lg border p-3" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)' }}>
            <dt className="text-xs" style={{ color: 'var(--vf-text-2)' }}>{k}</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums" style={{ color: 'var(--vf-text-1)' }}>{v}</dd>
          </div>
        ))}
      </dl>

      {view.unqualified.length > 0 && (
        <div role="alert" className="rounded-lg border p-4" style={{ borderColor: 'var(--vf-status-callback-fg)', background: 'var(--vf-yellow-bg)' }}>
          <p className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--vf-text-1)' }}>
            <AlertTriangle className="h-4 w-4" aria-hidden />
            {t('dialer:callPage.summary.unqualified', { count: view.unqualified.length })}
          </p>
          <ul className="mt-2 flex flex-col gap-1">
            {view.unqualified.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-2 text-sm" style={{ color: 'var(--vf-text-1)' }}>
                {c.name}
                <Button size="sm" variant="outline" onClick={() => onQualifyNow(c)}>{t('dialer:callPage.summary.qualifyNow')}</Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-sm font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:callPage.summary.meetingsList')}</h2>
          {meetings.length === 0 ? <p className="text-sm" style={{ color: 'var(--vf-text-3)' }}>—</p>
            : <ul className="text-sm" style={{ color: 'var(--vf-text-1)' }}>{meetings.map((m) => <li key={m.qualificationId}>{m.name}</li>)}</ul>}
          <Link className="mt-1 inline-block text-xs underline-offset-2 hover:underline" style={{ color: 'var(--vf-accent-text)' }} to="/ParallelDialer/followups?tab=agenda">
            {t('dialer:callPage.summary.agenda')}
          </Link>
        </div>
        <div>
          <h2 className="mb-1 text-sm font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:callPage.summary.callbacksList')}</h2>
          {callbacks.length === 0 ? <p className="text-sm" style={{ color: 'var(--vf-text-3)' }}>—</p>
            : <ul className="text-sm" style={{ color: 'var(--vf-text-1)' }}>{callbacks.map((m) => <li key={m.qualificationId}>{m.name}{m.followUpAt ? ` · ${new Date(m.followUpAt).toLocaleString()}` : ''}</li>)}</ul>}
          <Link className="mt-1 inline-block text-xs underline-offset-2 hover:underline" style={{ color: 'var(--vf-accent-text)' }} to="/ParallelDialer/followups">
            {t('dialer:callPage.summary.tasks')}
          </Link>
        </div>
      </div>

      {view.saved.length > 0 && (
        <div>
          <h2 className="mb-1 text-sm font-semibold" style={{ color: 'var(--vf-text-1)' }}>{t('dialer:callPage.summary.qualified')}</h2>
          <ul className="flex flex-col gap-1">
            {view.saved.map((q) => (
              <li key={q.qualificationId} className="flex items-center justify-between gap-2 text-sm" style={{ color: 'var(--vf-text-1)' }}>
                <span>{q.name} · {t(`dialer:callPage.outcomes.${q.outcome}`)}</span>
                <Button size="sm" variant="ghost" onClick={() => onCorrect({ id: q.parallelCallId, name: q.name })}>{t('dialer:callPage.summary.correct')}</Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onRestart}>{t('dialer:callPage.summary.restart')}</Button>
        <span className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('dialer:callPage.summary.remaining', { count: view.remaining })}</span>
        <div className="flex-1" />
        <Button variant="ghost" onClick={onBackToList}>{t('dialer:callPage.summary.toList')}</Button>
      </div>
    </section>
  );
}
